import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift } from 'lucide-react';
import { authApi } from '../../api/auth';
import { marketplaceApi } from '../../api/marketplace';
import { cardsApi } from '../../api/cards';
import { usersApi } from '../../api/users';
import CardItem from '../profile/CardItem';
import '../../styles/football-theme.css';
import { useSound } from '../../context/SoundContext';

const PackOpening = () => {
  const navigate = useNavigate();
  const { playSound } = useSound();
  const [user, setUser] = useState(null);
  const [isOpening, setIsOpening] = useState(false);
  const [allCards, setAllCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showCard, setShowCard] = useState(false);
  const [error, setError] = useState(null);
  const [hasOpened, setHasOpened] = useState(false);
  const [isLoadingCards, setIsLoadingCards] = useState(false);

  useEffect(() => {
    const currentUser = authApi.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    setUser(currentUser);
    
    // Check if onboarding is already completed, even when all cards are listed
    const checkIfAlreadyCompleted = async () => {
      try {
        const [userResponse, cardsResponse] = await Promise.all([
          usersApi.getById(currentUser.id),
          cardsApi.getUserCards(currentUser.id, {})
        ]);
        const starterPackReceivedCount = Number(
          userResponse?.user?.starter_pack_received_count ??
          userResponse?.user?.starterPackReceivedCount ??
          0
        );
        const hasReceivedStarterPack = userResponse?.user?.has_received_starter_pack === true ||
                                       userResponse?.user?.has_received_starter_pack === 1 ||
                                       userResponse?.user?.has_received_starter_pack === '1' ||
                                       starterPackReceivedCount >= 30;
        const hasCards = cardsResponse?.cards && cardsResponse.cards.length > 0;
        
        if (hasReceivedStarterPack || hasCards) {
          // User already has cards, onboarding is complete - redirect to home
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Error checking user cards:', error);
        // Continue if error (fail gracefully)
      }
    };
    
    checkIfAlreadyCompleted();
  }, [navigate]);

  // Remove automatic opening - user will click button

  // Load starter cards (30 basic cards)
  const loadStarterCards = async () => {
    // Get fresh user data from localStorage
    const currentUser = authApi.getCurrentUser();
    const userId = currentUser?.id || user?.id;
    
    if (!userId) {
      setError('User not found. Please login again.');
      setIsLoadingCards(false);
      setHasOpened(false);
      navigate('/login');
      return;
    }

    setIsLoadingCards(true);
    setError(null);
    playSound('packOpen');

    try {
      // Call API to get starter cards
      const response = await marketplaceApi.openStarterPack(userId);
      
      if (response?.success && response?.packOpening) {
        // Backend returns { success: true, packOpening: { userId, cards, count } }
        const cards = response.packOpening.cards || [];
        
        if (cards.length > 0) {
          setAllCards(cards);
          setCurrentCardIndex(0);
          setShowCard(true);
          setIsOpening(true);
          setIsLoadingCards(false);
          setHasOpened(true);
          playSound('cardReveal');
        } else {
          // If no cards returned but success, might already have cards
          throw new Error('No cards received. You may have already opened your starter pack.');
        }
      } else {
        throw new Error(response?.error || response?.message || 'Failed to load starter cards');
      }
    } catch (err) {
      console.error('Error loading starter cards:', err);
      playSound('error');
      const errorMessage = err.message || 'Failed to load starter cards. Please try again.';
      
      // If user already has starter cards, navigate to home
      if (errorMessage.includes('already received') || errorMessage.includes('already opened')) {
        setError('You have already received your starter cards. Redirecting to home...');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(errorMessage);
      }
      setIsLoadingCards(false);
    }
  };

  // Skip opening - assign all cards without showing them
  const handleSkip = async () => {
    playSound('click');
    // Get fresh user data from localStorage
    const currentUser = authApi.getCurrentUser();
    const userId = currentUser?.id || user?.id;
    
    if (!userId) {
      setError('User not found. Please login again.');
      navigate('/login');
      return;
    }

    setIsLoadingCards(true);
    setError(null);

    try {
      // Call API to assign starter cards (they will be assigned but not shown)
      const response = await marketplaceApi.openStarterPack(userId);
      
      if (response?.success) {
        // Cards are assigned, navigate directly to home
        navigate('/');
      } else {
        throw new Error(response?.message || 'Failed to assign starter cards');
      }
    } catch (err) {
      console.error('Error assigning starter cards:', err);
      const errorMessage = err.message || 'Failed to assign starter cards. Please try again.';
      
      // If user already has starter cards, navigate to home anyway
      if (errorMessage.includes('already received') || errorMessage.includes('already opened')) {
        navigate('/');
      } else {
        setError(errorMessage);
        setIsLoadingCards(false);
      }
    }
  };

  // Show next card
  const handleNextCard = () => {
    if (currentCardIndex < allCards.length - 1) {
      setShowCard(false);
      setTimeout(() => {
        setCurrentCardIndex(prev => prev + 1);
        setShowCard(true);
        playSound('cardReveal');
      }, 300);
    } else {
      // Last card, navigate to home
      playSound('success');
      navigate('/');
    }
  };

  // Show previous card
  const handlePreviousCard = () => {
    if (currentCardIndex > 0) {
      setShowCard(false);
      setTimeout(() => {
        setCurrentCardIndex(prev => prev - 1);
        setShowCard(true);
        playSound('click');
      }, 300);
    }
  };

  return (
    <div className="min-h-screen football-background stadium-lights flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {!isOpening ? (
          <div className="football-card glass-football p-8 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-6 trophy-glow">
              <Gift className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Welcome to DIOS10!</h2>
            <p className="text-gray-300 text-lg mb-8">
              You're about to receive <span className="text-yellow-400 font-bold">30 Starter Cards</span>
            </p>
            {error && (
              <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-6">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={loadStarterCards}
                disabled={!user || isLoadingCards}
                className="btn-football text-lg px-12 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingCards ? 'Loading Cards...' : 'Open Cards'}
              </button>
              <button
                onClick={handleSkip}
                disabled={!user || isLoadingCards}
                className="px-12 py-4 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 hover:text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingCards ? 'Assigning...' : 'Skip & Assign All'}
              </button>
            </div>
            <p className="text-gray-400 text-sm mt-4">
              All 30 cards will be assigned to your account. You can skip the opening animation if you prefer.
            </p>
          </div>
        ) : (
          <div className="text-center">
            {/* Card Counter */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Your Starter Cards</h2>
              <p className="text-gray-300">
                Card {currentCardIndex + 1} of {allCards.length}
              </p>
            </div>

            {/* Current Card Display - stessa vista di My Cards, senza bottoni Info/Carrello */}
            <div className="pack-container mb-8 profile-container" style={{ minHeight: '452px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {showCard && allCards.length > 0 && allCards[currentCardIndex] ? (
                <div className={`pack-card ${showCard ? 'flip' : ''}`}>
                  <div className="pack-card-front">
                    <div className="profile-gallery-card-scaled">
                      <CardItem
                        card={allCards[currentCardIndex]}
                        user={user}
                        hideHoverActions
                        isUsedInActiveGameweek={false}
                        onCardListed={() => {}}
                        hasOpenTrade={false}
                        openTrade={null}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="profile-gallery-card-scaled opacity-0 pointer-events-none">
                  <div className="profile-card-item football-card glass-football w-[303px] h-[452px] min-w-[303px] min-h-[452px] bg-gray-700 rounded" />
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={handlePreviousCard}
                disabled={currentCardIndex === 0}
                className="btn-football px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              
              <div className="scoreboard inline-block px-6 py-3">
                <div className="text-xl font-bold text-yellow-400">
                  {currentCardIndex + 1} / {allCards.length}
                </div>
              </div>

              <button
                onClick={handleNextCard}
                className="btn-football px-6 py-3"
              >
                {currentCardIndex === allCards.length - 1 ? 'Finish →' : 'Next →'}
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mt-8 w-full max-w-md mx-auto">
              <div className="w-full bg-gray-700 rounded-full h-4">
                <div
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${((currentCardIndex + 1) / allCards.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackOpening;

